/**
 * @cfleet/api-client - Customer Order Hooks
 *
 * React Query hooks for customer order management operations.
 * These endpoints require customer authentication.
 *
 * Orders represent delivery requests created by customers.
 * Operations include listing, viewing details, creating, cancelling,
 * and tracking orders.
 *
 * @example
 * ```tsx
 * import {
 *   useCustomerOrders,
 *   useCustomerOrder,
 *   useCustomerOrderTracking,
 *   useCreateCustomerOrder,
 *   useCancelCustomerOrder,
 *   useDeliveryEstimate,
 *   useAddressAutocomplete,
 *   useValidateAddress,
 * } from '@cfleet/api-client';
 *
 * // List orders with pagination
 * const { data } = useCustomerOrders({ page: 1, per_page: 20 }, { authConfig });
 *
 * // Create order
 * const { mutate: createOrder } = useCreateCustomerOrder({ authConfig });
 * createOrder({ organization_id: 'org_001', pickup: {...}, dropoff: {...} });
 * ```
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import createClient from 'openapi-fetch';
import type {
  CustomerOrdersReadComponents,
  CustomerOrdersReadPaths,
  CustomerOrdersCreateComponents,
  CustomerOrdersCreatePaths,
  CustomerOrdersActionsPaths,
  CustomerOrdersActionsComponents,
} from '../generated';
import { createAuthMiddleware, type AuthMiddlewareConfig } from '../middleware';

// ─────────────────────────────────────────────────────────────────────────────
// Type Aliases from Generated Types
// ─────────────────────────────────────────────────────────────────────────────

// Read types
type CustomerOrder = CustomerOrdersReadComponents['schemas']['CustomerOrder'];
type OrderListResponse = CustomerOrdersReadComponents['schemas']['OrderListResponse'];
type OrderTrackingResponse = CustomerOrdersReadComponents['schemas']['OrderTrackingResponse'];
type OrderStatus = CustomerOrdersReadComponents['schemas']['OrderStatus'];

// Create types
type CreateOrderRequest = CustomerOrdersCreateComponents['schemas']['CreateOrderRequest'];
type CreateOrderResponse = CustomerOrdersCreateComponents['schemas']['CreateOrderResponse'];
type DeliveryEstimate = CustomerOrdersCreateComponents['schemas']['DeliveryEstimate'];
type DeliveryEstimateRequest = CustomerOrdersCreateComponents['schemas']['DeliveryEstimateRequest'];
type AddressAutocompleteResponse =
  CustomerOrdersCreateComponents['schemas']['AddressAutocompleteResponse'];
type AddressValidationRequest =
  CustomerOrdersCreateComponents['schemas']['AddressValidationRequest'];
type AddressValidationResponse =
  CustomerOrdersCreateComponents['schemas']['AddressValidationResponse'];

// Action types
type CancelOrderRequest = CustomerOrdersActionsComponents['schemas']['CancelOrderRequest'];
type CancelOrderResponse = CustomerOrdersActionsComponents['schemas']['CancelOrderResponse'];

// Re-export types for consumers
export type {
  CustomerOrder,
  OrderListResponse,
  OrderTrackingResponse,
  OrderStatus,
  CreateOrderRequest,
  CreateOrderResponse,
  DeliveryEstimate,
  DeliveryEstimateRequest,
  AddressAutocompleteResponse,
  AddressValidationRequest,
  AddressValidationResponse,
  CancelOrderRequest,
  CancelOrderResponse,
};

// ─────────────────────────────────────────────────────────────────────────────
// Specialized Client Factories
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Creates a client specifically for customer orders read operations.
 * This avoids type conflicts from combining multiple path types.
 */
function createCustomerOrdersReadClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerOrdersReadPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

/**
 * Creates a client specifically for customer orders create operations.
 * This avoids type conflicts from combining multiple path types.
 */
function createCustomerOrdersCreateClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerOrdersCreatePaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

/**
 * Creates a client specifically for customer orders action operations.
 * This avoids type conflicts from combining multiple path types.
 */
function createCustomerOrdersActionsClient(authConfig: AuthMiddlewareConfig) {
  const client = createClient<CustomerOrdersActionsPaths>({
    baseUrl: authConfig.baseUrl,
  });
  client.use(createAuthMiddleware(authConfig));
  return client;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for customer order hooks
 */
export interface UseCustomerOrderHooksConfig {
  /** Authentication middleware configuration (required for authenticated endpoints) */
  authConfig: AuthMiddlewareConfig;
}

/**
 * Parameters for listing customer orders
 */
export interface ListCustomerOrdersParams {
  /** Page number (1-based) */
  page?: number;
  /** Number of items per page */
  per_page?: number;
  /** Filter by order status */
  status?: OrderStatus;
  /** Search by order ID, address, or recipient name */
  q?: string;
  /** Filter orders created on or after this date */
  from?: string;
  /** Filter orders created on or before this date */
  to?: string;
  /** Filter by partner organization */
  organization_id?: string;
  /** Field to sort by */
  sort_by?: 'created_at' | 'status' | 'price';
  /** Sort direction */
  sort_order?: 'asc' | 'desc';
}

/**
 * Query keys for customer order-related queries
 */
export const customerOrderQueryKeys = {
  all: ['customer-orders'] as const,
  lists: () => [...customerOrderQueryKeys.all, 'list'] as const,
  list: (params?: ListCustomerOrdersParams) => [...customerOrderQueryKeys.lists(), params] as const,
  details: () => [...customerOrderQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerOrderQueryKeys.details(), id] as const,
  tracking: (id: string) => [...customerOrderQueryKeys.detail(id), 'tracking'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerOrders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to list customer orders with pagination and filtering.
 *
 * Requires customer authentication. Results are scoped to the authenticated customer.
 *
 * @param params - Optional filter and pagination parameters
 * @param config - Configuration including auth config
 * @returns Query hook with paginated order list
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useCustomerOrders(
 *   { status: 'Placed', page: 1, per_page: 20 },
 *   { authConfig }
 * );
 *
 * return (
 *   <OrderTable
 *     orders={data?.data ?? []}
 *     pagination={data?.pagination}
 *   />
 * );
 * ```
 */
export function useCustomerOrders(
  params?: ListCustomerOrdersParams,
  config?: UseCustomerOrderHooksConfig
) {
  return useQuery({
    queryKey: customerOrderQueryKeys.list(params),
    queryFn: async (): Promise<OrderListResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createCustomerOrdersReadClient(config.authConfig);
      const { data, error } = await client.GET('/customer/orders', {
        params: { query: params },
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to fetch orders');
      }

      return data!;
    },
    enabled: !!config?.authConfig,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerOrder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get a single customer order by ID.
 *
 * Requires customer authentication. Customer can only access their own orders.
 *
 * @param orderId - Order ID (null/undefined disables query)
 * @param config - Configuration including auth config
 * @returns Query hook with order details
 *
 * @example
 * ```tsx
 * const { data: order, isLoading } = useCustomerOrder(orderId, { authConfig });
 *
 * if (isLoading) return <Spinner />;
 * return <OrderDetails order={order} />;
 * ```
 */
export function useCustomerOrder(
  orderId: string | null | undefined,
  config?: UseCustomerOrderHooksConfig
) {
  return useQuery({
    queryKey: customerOrderQueryKeys.detail(orderId || ''),
    queryFn: async (): Promise<CustomerOrder> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }
      if (!orderId) {
        throw new Error('Order ID required');
      }

      const client = createCustomerOrdersReadClient(config.authConfig);
      const { data, error } = await client.GET('/customer/orders/{orderId}', {
        params: { path: { orderId } },
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to fetch order');
      }

      return data!;
    },
    enabled: !!config?.authConfig && !!orderId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerOrderTracking
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get order tracking timeline.
 *
 * Returns the status history/tracking timeline for a specific order.
 * Shows all status changes with timestamps and optional location/notes.
 *
 * @param orderId - Order ID (null/undefined disables query)
 * @param config - Configuration including auth config
 * @returns Query hook with order tracking data
 *
 * @example
 * ```tsx
 * const { data: tracking } = useCustomerOrderTracking(orderId, { authConfig });
 *
 * return (
 *   <Timeline events={tracking?.events ?? []} />
 * );
 * ```
 */
export function useCustomerOrderTracking(
  orderId: string | null | undefined,
  config?: UseCustomerOrderHooksConfig
) {
  return useQuery({
    queryKey: customerOrderQueryKeys.tracking(orderId || ''),
    queryFn: async (): Promise<OrderTrackingResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }
      if (!orderId) {
        throw new Error('Order ID required');
      }

      const client = createCustomerOrdersReadClient(config.authConfig);
      const { data, error } = await client.GET('/customer/orders/{orderId}/tracking', {
        params: { path: { orderId } },
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to fetch order tracking');
      }

      return data!;
    },
    enabled: !!config?.authConfig && !!orderId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCreateCustomerOrder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to create a new customer order.
 *
 * Creates a new delivery order for the authenticated customer.
 * Order is initially created in "Placed" status.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for creating orders
 *
 * @example
 * ```tsx
 * const { mutate: createOrder, isPending } = useCreateCustomerOrder({ authConfig });
 *
 * const handleSubmit = (formData) => {
 *   createOrder({
 *     organization_id: 'org_001',
 *     pickup: { address: {...}, contact: {...} },
 *     dropoff: { address: {...}, contact: {...} },
 *   }, {
 *     onSuccess: (data) => navigate(`/orders/${data.order_id}`),
 *   });
 * };
 * ```
 */
export function useCreateCustomerOrder(config: UseCustomerOrderHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (orderData: CreateOrderRequest): Promise<CreateOrderResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createCustomerOrdersCreateClient(config.authConfig);
      const { data, error } = await client.POST('/customer/orders', {
        body: orderData,
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to create order');
      }

      return data!;
    },
    onSuccess: () => {
      // Invalidate orders list to refetch
      queryClient.invalidateQueries({ queryKey: customerOrderQueryKeys.lists() });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCancelCustomerOrder
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Input for cancel order mutation
 */
export interface CancelOrderInput {
  orderId: string;
  reason?: string;
}

/**
 * Hook to cancel a customer order.
 *
 * Only orders in Draft or Placed status can be cancelled.
 * Orders that are In Transit, Completed, or already Cancelled cannot be cancelled.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for cancelling orders
 *
 * @example
 * ```tsx
 * const { mutate: cancelOrder, isPending } = useCancelCustomerOrder({ authConfig });
 *
 * const handleCancel = () => {
 *   cancelOrder({
 *     orderId: 'ORD-2026-00001',
 *     reason: 'No longer needed',
 *   });
 * };
 * ```
 */
export function useCancelCustomerOrder(config: UseCustomerOrderHooksConfig) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, reason }: CancelOrderInput): Promise<CancelOrderResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createCustomerOrdersActionsClient(config.authConfig);
      const { data, error } = await client.PATCH('/customer/orders/{orderId}/cancel', {
        params: { path: { orderId } },
        body: reason ? { reason } : undefined,
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to cancel order');
      }

      return data!;
    },
    onSuccess: (_, variables) => {
      // Invalidate specific order and list
      queryClient.invalidateQueries({ queryKey: customerOrderQueryKeys.detail(variables.orderId) });
      queryClient.invalidateQueries({ queryKey: customerOrderQueryKeys.lists() });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useDeliveryEstimate
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get delivery estimate for given addresses.
 *
 * Calculates estimated delivery time and price for given pickup and dropoff addresses.
 * Use this endpoint before creating an order to show pricing to the customer.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for getting delivery estimates
 *
 * @example
 * ```tsx
 * const { mutate: getEstimate, data: estimate } = useDeliveryEstimate({ authConfig });
 *
 * const handleAddressChange = () => {
 *   getEstimate({
 *     pickup_address: { street: '123 Main St', city: 'Toronto', zip: 'M5J 2N8' },
 *     dropoff_address: { street: '456 Oak Ave', city: 'Toronto', zip: 'M4K 1P5' },
 *   });
 * };
 * ```
 */
export function useDeliveryEstimate(config: UseCustomerOrderHooksConfig) {
  return useMutation({
    mutationFn: async (request: DeliveryEstimateRequest): Promise<DeliveryEstimate> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createCustomerOrdersCreateClient(config.authConfig);
      const { data, error } = await client.POST('/customer/delivery/estimate', {
        body: request,
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to get delivery estimate');
      }

      return data!;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useAddressAutocomplete
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get address autocomplete suggestions.
 *
 * Returns address suggestions based on partial input.
 * Use debouncing in your component to avoid excessive API calls.
 *
 * @param query - Partial address string (null/undefined/empty disables query)
 * @param config - Configuration including auth config
 * @returns Query hook with address suggestions
 *
 * @example
 * ```tsx
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebounce(search, 300);
 *
 * const { data: suggestions } = useAddressAutocomplete(debouncedSearch, { authConfig });
 *
 * return (
 *   <Combobox
 *     value={search}
 *     onChange={setSearch}
 *     options={suggestions?.suggestions ?? []}
 *   />
 * );
 * ```
 */
export function useAddressAutocomplete(
  query: string | null | undefined,
  config?: UseCustomerOrderHooksConfig
) {
  return useQuery({
    queryKey: ['address-autocomplete', query] as const,
    queryFn: async (): Promise<AddressAutocompleteResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }
      if (!query) {
        throw new Error('Query required');
      }

      const client = createCustomerOrdersCreateClient(config.authConfig);
      const { data, error } = await client.GET('/customer/address/autocomplete', {
        params: { query: { query, limit: 5 } },
      });

      if (error) {
        throw new Error(
          (error as { detail?: string }).detail || 'Failed to get address suggestions'
        );
      }

      return data!;
    },
    enabled: !!config?.authConfig && !!query && query.length >= 3,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useValidateAddress
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to validate and normalize an address.
 *
 * Validates an address and returns normalized/corrected version with coordinates.
 * Use this endpoint to verify an address before creating an order.
 *
 * @param config - Configuration including auth config
 * @returns Mutation hook for validating addresses
 *
 * @example
 * ```tsx
 * const { mutate: validateAddress } = useValidateAddress({ authConfig });
 *
 * const handleValidate = (address) => {
 *   validateAddress(address, {
 *     onSuccess: (result) => {
 *       if (result.is_valid) {
 *         setNormalizedAddress(result.normalized_address);
 *       } else {
 *         setError(result.issues);
 *       }
 *     },
 *   });
 * };
 * ```
 */
export function useValidateAddress(config: UseCustomerOrderHooksConfig) {
  return useMutation({
    mutationFn: async (request: AddressValidationRequest): Promise<AddressValidationResponse> => {
      if (!config?.authConfig) {
        throw new Error('Auth config required');
      }

      const client = createCustomerOrdersCreateClient(config.authConfig);
      const { data, error } = await client.POST('/customer/address/validate', {
        body: request,
      });

      if (error) {
        throw new Error((error as { detail?: string }).detail || 'Failed to validate address');
      }

      return data!;
    },
  });
}
